import { useCallback, useEffect, useState } from 'react';
import { GAME_MODES, GAME_STATUSES } from '@blind-kings/game-core';
import Modal from '../../Components/Modal';
import ModalButton from '../../Components/ModalButton';
import BkButton from '../../Components/ui/BkButton';
import { useGameSession } from '../../contexts/GameSessionContext';
import useDisplayPieceValues from '../../hooks/use-display-piece-values';
import EmoteWheel from './EmoteWheel';
import FullGameBoard from './FullGameBoard';
import GameHeader from './GameHeader';
import GameOverModal, { type GameRewards } from './GameOverModal';
import SeatEmoteBurst from './SeatEmoteBurst';
import useGameState, { type GameRecord } from './hooks/use-game-state';

export type GameProps = {
  game: GameRecord;
  applyGameUpdate?: (payload: GameRecord) => void;
};

export default function Game({ game, applyGameUpdate }: GameProps) {
  const {
    blindKingsGame,
    canvasBoard,
    authoritativeGame,
    rewards,
    selectedPiece,
    leaving,
    showLeaveGameModal,
    renderVertically,
    boardAreaRef,
    setShowLeaveGameModal,
    selectPiece,
    handleLeave,
    handleReset,
    canReset,
    resetting,
    skinId,
    pendingFlipPieceId,
    localAction,
    remoteFocus,
    onPlayComplete,
    inputLocked,
  } = useGameState({ game, applyGameUpdate });
  const { setSession } = useGameSession();
  const { displayPieceValues } = useDisplayPieceValues();
  const [resultsOpen, setResultsOpen] = useState(false);
  const [resultsDismissed, setResultsDismissed] = useState(false);

  const { isGameOver, userIsInGame, firstPlayer, secondPlayer, gameMode } =
    authoritativeGame;
  const matchOver =
    Boolean(isGameOver) || String(game.status) === GAME_STATUSES.FINISHED;
  const gameId = String(game.gameId ?? '');
  const casualMatch =
    gameMode === GAME_MODES.LOCAL_GAME || gameMode === GAME_MODES.AI_GAME;

  const firstEmote = (
    firstPlayer as { recentEmote?: { emoteId?: string; at?: number } } | undefined
  )?.recentEmote;
  const secondEmote = (
    secondPlayer as { recentEmote?: { emoteId?: string; at?: number } } | undefined
  )?.recentEmote;

  const handleResultsClose = useCallback(() => {
    setResultsOpen(false);
  }, []);

  const requestLeave = useCallback(() => {
    setShowLeaveGameModal(true);
  }, [setShowLeaveGameModal]);

  useEffect(() => {
    setSession({
      canReset,
      isGameOver: matchOver,
      leaving,
      resetting,
      requestLeave,
      resetGame: canReset ? () => void handleReset() : undefined,
    });
  }, [
    canReset,
    handleReset,
    leaving,
    matchOver,
    requestLeave,
    resetting,
    setSession,
  ]);

  useEffect(() => () => setSession(null), [setSession]);

  return (
    <>
      <Modal
        show={showLeaveGameModal}
        onClose={() => setShowLeaveGameModal(false)}
        title={casualMatch ? 'Leave Game' : 'Forfeit Game'}
        bottom={(
          <div className="mt-5 flex flex-row justify-between space-x-2">
            <ModalButton
              onClick={() => setShowLeaveGameModal(false)}
              disabled={leaving}
              text="Cancel"
            />
            <ModalButton
              danger
              disabled={leaving}
              text={leaving ? 'Leaving...' : 'Leave'}
              onClick={handleLeave}
            />
          </div>
        )}
      >
        <div>
          {casualMatch
            ? 'Return home? This match will still be here when you come back.'
            : 'Leaving now will mean defeat. Are you sure you want to leave?'}
        </div>
      </Modal>
      <div className="relative flex h-full min-h-0 w-full select-none flex-col gap-1.5 overflow-hidden px-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-1 sm:gap-2 sm:px-3 sm:pt-2 md:px-4 md:pb-3 lg:px-8 xl:px-16">
        <GameHeader
          blindKingsGame={blindKingsGame}
          firstBurst={
            <SeatEmoteBurst emoteId={firstEmote?.emoteId} at={firstEmote?.at} />
          }
          secondBurst={
            <SeatEmoteBurst emoteId={secondEmote?.emoteId} at={secondEmote?.at} />
          }
          emote={
            userIsInGame && !matchOver ? (
              <EmoteWheel
                gameId={gameId}
                onSent={(payload) => {
                  applyGameUpdate?.(payload as GameRecord);
                }}
              />
            ) : null
          }
          trailing={
            matchOver && resultsDismissed && !resultsOpen ? (
              <BkButton
                variant="secondary"
                size="sm"
                aria-label="Show match results"
                onClick={() => setResultsOpen(true)}
              >
                Results
              </BkButton>
            ) : null
          }
        />
        <div ref={boardAreaRef} className="relative min-h-0 flex-1">
          <FullGameBoard
            displayPieceValues={displayPieceValues}
            displayPieceInfo
            renderVertically={renderVertically}
            blindKingsGame={blindKingsGame}
            canvasBoard={canvasBoard}
            selectPiece={selectPiece}
            selectedPiece={selectedPiece}
            skinId={skinId}
            pendingFlipPieceId={pendingFlipPieceId}
            localAction={localAction}
            remoteFocus={remoteFocus}
            onPlayComplete={onPlayComplete}
            inputLocked={inputLocked}
          />
        </div>
      </div>
      <GameOverModal
        blindKingsGame={authoritativeGame}
        rewards={rewards as GameRewards | undefined}
        ready={!resetting}
        ended={matchOver}
        gameId={gameId}
        forceOpen={resultsOpen}
        onClose={handleResultsClose}
        onDismissedChange={setResultsDismissed}
      />
    </>
  );
}
